import React, { useEffect, useMemo, useState } from 'react'
import { Form, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import styles from './preferences-form.module.scss'
import ButtonComponent from '@BUP/components/Generics/Buttons/button-component'

const { Option } = Select

/**
 * Standalone preferences picker for "Asignación de Cursos" (INPUT stage).
 * Adapts the same Select+search pattern as MultipleSelectCardSociogram, but as a single-step
 * form (no pages/dependencies) with a hard cap of `maxSelection` classmates.
 */
const PreferencesForm = ({
  classmatesOptions,
  initialSelection,
  maxSelection = 5,
  isPeriodOpen,
  onSubmit,
  isSubmitting
}) => {
  const [t] = useTranslation('translation', { keyPrefix: 'pages.data.courseAssignmentPreferences' })
  const [form] = Form.useForm()
  const [selectedIds, setSelectedIds] = useState(initialSelection || [])
  const [courseFilter, setCourseFilter] = useState(null)

  // Distinct course/section names among eligible classmates (e.g. "1°A", "1°B") - lets a student
  // narrow the dropdown to their own or a specific section when the grade has many students across
  // several sections, instead of scrolling/searching the whole list.
  const courseOptions = useMemo(() => {
    const names = [...new Set(classmatesOptions.map(classmate => classmate.courseName).filter(Boolean))]
    return names.sort((a, b) => a.localeCompare(b, 'es', { numeric: true }))
  }, [classmatesOptions])

  // Which <Option>s to actually render: the chosen course, plus anyone already picked - rc-select
  // only applies `filterOption` to the active search text, not to the base list it shows when the
  // dropdown is opened with no search typed yet, so the course filter has to work by not rendering
  // the other courses' <Option>s at all. An already-picked classmate from a different course must
  // still get an <Option> here, or their selected tag has nothing to read its label from.
  const visibleClassmates = useMemo(() => {
    if (!courseFilter) return classmatesOptions
    return classmatesOptions.filter(classmate =>
      classmate.courseName === courseFilter || selectedIds.includes(classmate.enrollmentId))
  }, [classmatesOptions, courseFilter, selectedIds])

  useEffect(() => {
    setSelectedIds(initialSelection || [])
    form.setFieldsValue({ classmateEnrollmentIds: initialSelection || [] })
  }, [initialSelection, form])

  const handleChange = (values) => {
    // antd 4.12.3 (this project's version) has no `maxCount` on Select; block the extra pick by hand.
    if (values.length > maxSelection) return
    setSelectedIds(values)
    form.setFieldsValue({ classmateEnrollmentIds: values })
  }

  const hasChanges = useMemo(() => {
    const sortedInitial = [...(initialSelection || [])].sort()
    const sortedSelected = [...selectedIds].sort()
    return JSON.stringify(sortedInitial) !== JSON.stringify(sortedSelected)
  }, [initialSelection, selectedIds])

  return (
    <div className={styles.container} translate='no'>
      <div className={styles.question_text_wrapper}>
        <h5>{t('questionText')}</h5>
      </div>
      <Form
        form={form}
        onFinish={values => onSubmit(values.classmateEnrollmentIds)}
        initialValues={{ classmateEnrollmentIds: initialSelection || [] }}
      >
        <div id='courseAssignmentAreaSelect'>
          {courseOptions.length > 1 && (
            <div className={styles.course_filter_wrapper}>
              <Select
                allowClear
                aria-label='course-filter'
                placeholder={t('courseFilterPlaceholder')}
                value={courseFilter}
                onChange={setCourseFilter}
                style={{ width: '100%' }}
                getPopupContainer={() => document.getElementById('courseAssignmentAreaSelect')}
              >
                {courseOptions.map(courseName => (
                  <Option key={`course-filter-${courseName}`} value={courseName}>
                    {courseName}
                  </Option>
                ))}
              </Select>
            </div>
          )}
          <Form.Item
            name='classmateEnrollmentIds'
            rules={[
              {
                required: true,
                type: 'array',
                min: 1,
                max: maxSelection,
                message: t('requiredResponse', { max: maxSelection })
              }
            ]}
          >
            {/* Select must be Form.Item's direct child - antd injects value/onChange onto it;
                wrapping it in another element (as the sociogram original does) breaks that
                injection and the Select never reflects a pre-filled/controlled value. */}
            <Select
              mode='multiple'
              allowClear
              aria-label='classmates-select'
              disabled={!isPeriodOpen}
              showSearch
              style={{ width: '100%' }}
              className={styles.select}
              onChange={handleChange}
              filterOption={(input, option) =>
                (option['data-name'] ?? '').toLowerCase().includes(input.toLowerCase())}
              optionLabelProp='children'
              optionFilterProp='children'
              getPopupContainer={() => document.getElementById('courseAssignmentAreaSelect')}
            >
              {visibleClassmates.map(classmate => (
                <Option
                  key={`classmate-${classmate.enrollmentId}`}
                  value={classmate.enrollmentId}
                  data-name={classmate.name}
                  data-test={classmate.enrollmentId}
                >
                  {classmate.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
        <p className={styles.counter}>
          {t('selectedCount', { count: selectedIds.length, max: maxSelection })}
        </p>
        <div className={styles.buttons_wrapper}>
          <ButtonComponent
            buttonText={t('saveButton')}
            colorType='primary'
            htmlType='submit'
            disabled={!isPeriodOpen || !hasChanges || isSubmitting}
          />
        </div>
      </Form>
    </div>
  )
}

export default PreferencesForm
